import { Facebook, MicVocal } from "lucide-react";
import { useAnimation, useInView } from "framer-motion";
import { useRef, useEffect } from "react";
import PaperPlaneAnimation from "./PaperPlaneAnimation";

export default function ContactSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  useEffect(() => {
    if (inView) {
      controls.start("animate");
    }
  }, [inView, controls]);

  const logoStyle = "p-2 text-white rounded-full bg-rouge hover:bg-white hover:text-rouge transition-colors duration-500";

  return (
    <section
      id="contact"
      className="pb-12 bg-white overflow-hidden relative"
    >
      <div className="container mx-auto px-4 flex flex-col items-center text-center relative">
        {/* Animation avion */}
        <PaperPlaneAnimation />

        {/* Contenu */}
        <div
          className="container mx-auto px-4 text-center relative z-10 -mt-20"
          ref={ref}
        >
          <h2 className="text-6xl mb-6 text-center text-orange font-bebas">
            Contact
          </h2>

          <p className="text-bleuFonce text-lg mb-8">
            Pour toute question, écrivez-nous à
            <br />
            <a
              href="mailto:fme@lagaronne.fr"
              className="text-rouge font-semibold hover:text-orange transition-colors duration-500"
            >
              fme@lagaronne.fr
            </a>
          </p>

          {/* Réseaux sociaux */}
          <div className="flex justify-center gap-6">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/FestivalFME"
              target="_blank"
              rel="noopener noreferrer"
              className={logoStyle}
              title="Suivez-nous sur Facebook"
            >
              <Facebook size={28} strokeWidth={1} fill="currentColor" />
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/fme_lagaronne"
              target="_blank"
              rel="noopener noreferrer"
              className={logoStyle}
              title="Suivez-nous sur Instagram"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="img"
                className="w-7 h-7"
              >
                <path d="M7.0301.084c-1.2768.0602-2.1487.264-2.911.5634-.7888.3075-1.4575.72-2.1228 1.3877-.6652.6677-1.075 1.3368-1.3802 2.127-.2954.7638-.4956 1.6365-.552 2.914-.0564 1.2775-.0689 1.6882-.0626 4.947.0062 3.2586.0206 3.6671.0825 4.9473.061 1.2765.264 2.1482.5635 2.9107.308.7889.72 1.4573 1.388 2.1228.6679.6655 1.3365 1.0743 2.1285 1.38.7632.295 1.6361.4961 2.9134.552 1.2773.056 1.6884.069 4.9462.0627 3.2578-.0062 3.668-.0207 4.9478-.0814 1.28-.0607 2.147-.2652 2.9098-.5633.7889-.3086 1.4578-.72 2.1228-1.3881.665-.6682 1.0745-1.3378 1.3795-2.1284.2957-.7632.4966-1.636.552-2.9124.056-1.2809.0692-1.6898.063-4.948-.0063-3.2583-.021-3.6668-.0817-4.9465-.0607-1.2797-.264-2.1487-.5633-2.9117-.3084-.7889-.72-1.4568-1.3876-2.1228C21.2982 1.33 20.628.9208 19.8378.6165 19.074.321 18.2017.1197 16.9244.0645 15.6471.0093 15.236-.005 11.977.0014 8.718.0076 8.31.0215 7.0301.0839m.1402 21.6932c-1.17-.0509-1.8053-.2453-2.2287-.408-.5606-.216-.96-.4771-1.3819-.895-.422-.4178-.6811-.8186-.9-1.378-.1644-.4234-.3624-1.058-.4171-2.228-.0595-1.2645-.072-1.6442-.079-4.848-.007-3.2037.0053-3.583.0607-4.848.05-1.169.2456-1.805.408-2.2282.216-.5613.4762-.96.895-1.3816.4188-.4217.8184-.6814 1.3783-.9003.423-.1651 1.0575-.3614 2.227-.4171 1.2655-.06 1.6447-.072 4.848-.079 3.2033-.007 3.5835.005 4.8495.0608 1.169.0508 1.8053.2445 2.228.408.5608.216.96.4754 1.3816.895.4217.4194.6816.8176.9005 1.3787.1653.4217.3617 1.056.4169 2.2263.0602 1.2655.0739 1.645.0796 4.848.0058 3.203-.0055 3.5834-.061 4.848-.051 1.17-.245 1.8055-.408 2.2294-.216.5604-.4763.96-.8954 1.3814-.419.4215-.8181.6811-1.3783.9-.4224.1649-1.0577.3617-2.2262.4174-1.2656.0595-1.6448.072-4.8493.079-3.2045.007-3.5825-.006-4.848-.0608M16.953 5.5864A1.44 1.44 0 1 0 18.39 4.144a1.44 1.44 0 0 0-1.437 1.4424M5.8385 12.012c.0067 3.4032 2.7706 6.1557 6.173 6.1493 3.4026-.0065 6.157-2.7701 6.1506-6.1733-.0065-3.4032-2.771-6.1565-6.174-6.1498-3.403.0067-6.156 2.771-6.1496 6.1738M8 12.0077a4 4 0 1 1 4.008 3.9921A3.9996 3.9996 0 0 1 8 12.0077"/>
              </svg>
            </a>

            {/* Spotify */}
            <a
              href="https://open.spotify.com/intl-fr/artist/6IcVpdzD5mnbMt0KOKcRvG?si=uRes6AcxTbK75wzMFKy2cQ"
              target="_blank"
              rel="noopener noreferrer"
              className={logoStyle}
              title="Écouter sur Spotify"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="img"
                className="w-7 h-7"
              >
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
              </svg>
            </a>

            {/* Deezer */}
            {/* <a
              href="https://open.spotify.com/intl-fr/artist/6IcVpdzD5mnbMt0KOKcRvG?si=uRes6AcxTbK75wzMFKy2cQ"
              target="_blank"
              rel="noopener noreferrer"
              className={logoStyle}
              title="Écouter sur Deezer"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
	              viewBox="0 0 306 358"
                fill="currentColor"
                role="img"
                className="w-7 h-7"
              >
                <g>
                  <path d="M22.7,115.7c4.4,0,8-14.5,8-32.4c0-17.9-3.6-32.4-8-32.4c-4.4,0-8,14.5-8,32.4C14.7,101.2,18.3,115.7,22.7,115.7z"/>
                  <path d="M257.6,18c-4.2,0-7.9,9.3-10.5,24.2C242.9,16.7,236.2,0,228.6,0c-9,0-16.9,23.5-20.6,57.7c-3.6-24.9-9.1-40.8-15.3-40.8
                    c-8.6,0-16,31.2-18.7,74.7c-5.1-22.3-12.5-36.3-20.7-36.3c-8.2,0-15.6,14-20.7,36.3C129.8,48.2,122.5,17,113.8,17
                    c-6.2,0-11.7,15.9-15.3,40.8C94.8,23.5,87,0,77.9,0c-7.6,0-14.4,16.7-18.5,42.3C56.9,27.4,53.1,18,48.9,18
                    c-7.8,0-14.1,32.4-14.1,72.4c0,40,6.3,72.4,14.1,72.4c3.2,0,6.2-5.5,8.5-14.7c3.7,33.8,11.5,57,20.5,57c7,0,13.2-13.9,17.4-35.9
                    c2.9,41.8,10.1,71.5,18.5,71.5c5.3,0,10.1-11.8,13.7-30.9c4.3,39.5,14.2,67.2,25.8,67.2s21.5-27.7,25.8-67.2
                    c3.6,19.1,8.4,30.9,13.7,30.9c8.4,0,15.6-29.7,18.5-71.5c4.2,22,10.4,35.9,17.4,35.9c9,0,16.8-23.2,20.5-57
                    c2.4,9.2,5.3,14.7,8.5,14.7c7.8,0,14.1-32.4,14.1-72.4C271.7,50.5,265.4,18,257.6,18L257.6,18z"/>
                  <path d="M283.8,115.7c4.4,0,8-14.5,8-32.4c0-17.9-3.6-32.4-8-32.4s-8,14.5-8,32.4C275.8,101.2,279.4,115.7,283.8,115.7z"/>
                </g>
                <g>
                  <path d="M58,357.5h43.1v-14.1H78.3v-6.5h21.4v-11.6H78.3v-6.1h22.8v-14.1H58V357.5z"/>
                  <path d="M106,357.5h43.1v-14.1h-22.8v-6.5h21.4v-11.6h-21.4v-6.1h22.8v-14.1H106V357.5z"/>
                  <path d="M305.6,357.5c-2.9-8-7.1-16.6-12.6-26.1c6.5-1.9,10.3-6,10.3-12.2c0-9.5-8.8-14-22.7-14h-28.6v52.4h20.4v-21.7
                    c4.5,7.6,7.9,14.8,10.1,21.7H305.6z M272.6,328.5v-9.3h7.1c3,0,4.7,1.6,4.7,4.6c0,3-1.7,4.6-4.7,4.6H272.6z"/>
                  <path d="M204.1,357.5h43.1v-14.1h-22.8v-6.5h21.4v-11.6h-21.4v-6.1h22.8v-14.1h-43.1V357.5z"/>
                  <path d="M154,319.3h21.1c-8.7,7.1-15.9,15.2-21.5,24.1v14.1h46.3v-14.1h-23.2c5.3-7.7,12.7-15.4,23.2-24.1v-14.1H154V319.3z"/>
                  <path d="M27.1,305.1H0.8v52.4h26.3c16.3,0,27.8-10.8,27.8-26.2C54.8,315.9,43.3,305.1,27.1,305.1z M26,343.3h-4.8v-24.1H26
                    c5.1,0,7.8,3.4,7.8,12C33.8,339.9,31.1,343.3,26,343.3z"/>
                </g>
              </svg>
            </a> */}

            {/* Apple Music */}
            {/* <a
              href="https://open.spotify.com/intl-fr/artist/6IcVpdzD5mnbMt0KOKcRvG?si=uRes6AcxTbK75wzMFKy2cQ"
              target="_blank"
              rel="noopener noreferrer"
              className={logoStyle}
              title="Écouter sur Apple Music"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                role="img"
                className="w-7 h-7"
              >
                <path d="M23.994 6.124a9.23 9.23 0 00-.24-2.19c-.317-1.31-1.062-2.31-2.18-3.043a5.022 5.022 0 00-1.877-.726 10.496 10.496 0 00-1.564-.15c-.04-.003-.083-.01-.124-.013H5.986c-.152.01-.303.017-.455.026-.747.043-1.49.123-2.193.4-1.336.53-2.3 1.452-2.865 2.78-.192.448-.292.925-.363 1.408-.056.392-.088.785-.1 1.18 0 .032-.007.062-.01.093v12.223c.01.14.017.283.027.424.05.815.154 1.624.497 2.373.65 1.42 1.738 2.353 3.234 2.801.42.127.856.187 1.293.228.555.053 1.11.06 1.667.06h11.03a12.5 12.5 0 001.57-.1c.822-.106 1.596-.35 2.295-.81a5.046 5.046 0 001.88-2.207c.186-.42.293-.87.37-1.324.113-.675.138-1.358.137-2.04-.002-3.8 0-7.595-.003-11.393zm-6.423 3.99v5.712c0 .417-.058.827-.244 1.206-.29.59-.76.962-1.388 1.14-.35.1-.706.157-1.07.173-.95.045-1.773-.6-1.943-1.536a1.88 1.88 0 011.038-2.022c.323-.16.67-.25 1.018-.324.378-.082.758-.153 1.134-.24.274-.063.457-.23.51-.516a.904.904 0 00.02-.193c0-1.815 0-3.63-.002-5.443a.725.725 0 00-.026-.185c-.04-.15-.15-.243-.304-.234-.16.01-.318.035-.475.066-.76.15-1.52.303-2.28.456l-2.325.47-1.374.278c-.016.003-.032.01-.048.013-.277.077-.377.203-.39.49-.002.042 0 .086 0 .13-.002 2.602 0 5.204-.003 7.805 0 .42-.047.836-.215 1.227-.278.64-.77 1.04-1.434 1.233-.35.1-.71.16-1.075.172-.96.036-1.755-.6-1.92-1.544-.14-.812.23-1.685 1.154-2.075.357-.15.73-.232 1.108-.31.287-.06.575-.116.86-.177.383-.083.583-.323.6-.714v-.15c0-2.96 0-5.922.002-8.882 0-.123.013-.25.042-.37.07-.285.273-.448.546-.518.255-.066.515-.112.774-.165.733-.15 1.466-.296 2.2-.444l2.27-.46c.67-.134 1.34-.27 2.01-.403.22-.043.442-.088.663-.106.31-.025.523.17.554.482.008.073.012.148.012.223.002 1.91.002 3.822 0 5.732z"/>
              </svg>
            </a> */}
          </div>

          <div className="flex justify-center mt-12">
            <a
              href="/documents/FME_Dossier_de_Presse.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-rouge text-white font-bebas font-bold text-2xl pl-3 pr-6 py-3 rounded-md shadow-lg hover:bg-white hover:text-rouge transition-colors duration-500 flex items-center justify-center"
            >
              <MicVocal size={20} className="mr-2"/> Dossier de Presse
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
